import { useEffect } from "react";
import { User as UserIcon, Lock } from "lucide-react";
import { useResponsibleOptions } from "@/hooks/useClient360Tasks";

/**
 * 3A Q3 — role-limited responsible select. manager → only self (rendered locked,
 * no free dropdown); supervisor → self+team; director/admin → all active users.
 * Options + restriction are backend-owned (AC-6/AC-10).
 */
export function ResponsibleSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const { data, isPending } = useResponsibleOptions(true);

  // Default to backend defaultId (self) once loaded.
  useEffect(() => {
    if (data && !value) onChange(data.defaultId);
  }, [data, value, onChange]);

  if (isPending || !data) {
    return (
      <div className="h-9 w-full animate-pulse rounded-md border bg-muted/40" aria-hidden />
    );
  }

  // Manager scope (only self) — show a locked field, never a dropdown.
  if (data.restricted || data.options.length <= 1) {
    const me = data.options.find((o) => o.id === data.defaultId) ?? data.options[0];
    return (
      <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2 text-sm">
        <span className="flex items-center gap-2 truncate">
          <UserIcon className="h-4 w-4 text-muted-foreground" aria-hidden /> {me?.name ?? "Я"}
        </span>
        <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      </div>
    );
  }

  return (
    <select
      value={value || data.defaultId}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {data.options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.id === data.defaultId ? `${o.name} (я)` : o.name}
        </option>
      ))}
    </select>
  );
}
